import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react';

import { format } from 'date-fns';
import { ClipboardList, GitCommit } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';

import type { Commit, TimeEntry } from '@/pages/developer/types';

export type WorkorderCommitsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;

  workorder: TimeEntry | null;
  commits: Commit[];

  selectedCommitShas: string[];
  setSelectedCommitShas: Dispatch<SetStateAction<string[]>>;

  rate: string;
  setRate: Dispatch<SetStateAction<string>>;
  hours: string;
  setHours: Dispatch<SetStateAction<string>>;
  hourlyRate: string;
  setHourlyRate: Dispatch<SetStateAction<string>>;
  misc: string;
  setMisc: Dispatch<SetStateAction<string>>;
  taxes: string;
  setTaxes: Dispatch<SetStateAction<string>>;

  onSave: () => Promise<void>;
  saving: boolean;

  onDeleteGitOrder?: (gitOrderId: string) => Promise<void>;
};

export function WorkorderCommitsDialog({
  open,
  onOpenChange,
  workorder,
  commits,
  selectedCommitShas,
  setSelectedCommitShas,
  rate,
  setRate,
  hours,
  setHours,
  hourlyRate,
  setHourlyRate,
  misc,
  setMisc,
  taxes,
  setTaxes,
  onSave,
  saving,
  onDeleteGitOrder,
}: WorkorderCommitsDialogProps) {
  const normalizedSelected = new Set(selectedCommitShas.map((sha) => sha.trim()).filter(Boolean));

  const [commitSearch, setCommitSearch] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [commitsVisible, setCommitsVisible] = useState(true);

  const isInvoiced = Boolean(workorder && (workorder.stage === 'invoiced' || workorder.invoiceJournalEntryId));
  const canEditWorkorder = Boolean(workorder) && !isInvoiced && !saving && !deleting;

  useEffect(() => {
    if (!open) return;
    setCommitSearch('');
    setCommitsVisible(true);
  }, [open, workorder?.id]);

  const filteredCommits = useMemo(() => {
    const query = commitSearch.trim().toLowerCase();
    if (!query) return commits;
    return commits.filter((commit) => {
      const sha = (commit.sha ?? '').toLowerCase();
      const message = (commit.message ?? '').toLowerCase();
      const author = (commit.author ?? '').toLowerCase();
      return sha.includes(query) || message.includes(query) || author.includes(query);
    });
  }, [commits, commitSearch]);

  const selectAll = () => {
    if (!canEditWorkorder) return;
    const eligible = filteredCommits
      .filter((commit) => {
        const otherWorkorderId = (commit.workorderId ?? '').trim();
        return !otherWorkorderId || otherWorkorderId === workorder?.id;
      })
      .map((commit) => commit.sha);
    setSelectedCommitShas(Array.from(new Set(eligible)));
  };

  const clearAll = () => {
    if (!canEditWorkorder) return;
    setSelectedCommitShas([]);
  };

  const workorderDateLabel = workorder?.date ? format(new Date(workorder.date), 'MMM d, yyyy') : '—';
  const selectedCount = normalizedSelected.size;

  const canDeleteGitOrder = Boolean(workorder && onDeleteGitOrder);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-primary" />
            Git order commits
          </DialogTitle>
          <DialogDescription>
            Select from <span className="font-medium">unassigned commits</span> (plus anything already attached) to update this Git order.
          </DialogDescription>
        </DialogHeader>

        {!workorder ? (
          <div className="rounded-md border border-border/60 bg-muted/20 p-4 text-sm text-muted-foreground">
            No Git order selected.
          </div>
        ) : commits.length === 0 ? (
          <div className="rounded-md border border-border/60 bg-muted/20 p-4 text-sm text-muted-foreground">
            No unassigned commits available.
          </div>
        ) : (
          <>
            {isInvoiced && (
              <div className="rounded-lg border border-border/60 bg-muted/10 p-3 text-sm text-muted-foreground">
                This Git order is invoiced. Commits and rates are locked.
              </div>
            )}

            <div className="grid grid-cols-1 gap-3 rounded-lg border border-border/60 bg-muted/10 p-3 sm:grid-cols-3 lg:grid-cols-6">
              <div>
                <p className="text-xs text-muted-foreground">Qty</p>
                <p className="text-sm font-medium">{selectedCount}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Hours</p>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={hours}
                  onChange={(event) => setHours(event.target.value)}
                  disabled={!canEditWorkorder}
                />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Hourly rate</p>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={hourlyRate}
                  onChange={(event) => setHourlyRate(event.target.value)}
                  disabled={!canEditWorkorder}
                />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Commit rate</p>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={rate}
                  onChange={(event) => setRate(event.target.value)}
                  disabled={!canEditWorkorder}
                />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Misc</p>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={misc}
                  onChange={(event) => setMisc(event.target.value)}
                  disabled={!canEditWorkorder}
                />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Taxes</p>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={taxes}
                  onChange={(event) => setTaxes(event.target.value)}
                  disabled={!canEditWorkorder}
                />
              </div>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-muted-foreground">
                Commits already assigned to a different Git order are locked.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setCommitsVisible((prev) => !prev)}
                >
                  {commitsVisible ? 'Hide commits' : 'Show commits'}
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={selectAll} disabled={!canEditWorkorder}>
                  Select all
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={clearAll} disabled={!canEditWorkorder}>
                  Clear
                </Button>
                {canDeleteGitOrder && (
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={async () => {
                      if (!workorder || !onDeleteGitOrder) return;
                      const ok = window.confirm(
                        'Delete this Git order? This will remove the Git order and unassign its commits.'
                      );
                      if (!ok) return;
                      setDeleting(true);
                      try {
                        await onDeleteGitOrder(workorder.id);
                        onOpenChange(false);
                      } finally {
                        setDeleting(false);
                      }
                    }}
                    disabled={!workorder || saving || deleting}
                  >
                    {deleting ? 'Deleting…' : 'Delete git order'}
                  </Button>
                )}
              </div>
            </div>

            {commitsVisible && (
              <>
                <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <Input
                    value={commitSearch}
                    onChange={(event) => setCommitSearch(event.target.value)}
                    placeholder="Search commits (sha, message, author)"
                  />
                  <p className="text-xs text-muted-foreground sm:text-right">
                    Showing <span className="font-medium text-foreground">{filteredCommits.length}</span> of{' '}
                    <span className="font-medium text-foreground">{commits.length}</span>
                  </p>
                </div>

                <div className="mt-3 rounded-lg border border-border overflow-hidden">
                  <div className="sm:hidden">
                    <div className="divide-y divide-border">
                      {filteredCommits.length === 0 ? (
                        <div className="p-4 text-sm text-muted-foreground">No matching commits.</div>
                      ) : (
                        filteredCommits.map((commit) => {
                        const otherWorkorderId = (commit.workorderId ?? '').trim();
                        const isLocked = isInvoiced || (!!otherWorkorderId && otherWorkorderId !== workorder?.id);
                        const checked = normalizedSelected.has(commit.sha);

                        return (
                          <button
                            key={commit.sha}
                            type="button"
                            disabled={isLocked}
                            className="w-full p-4 text-left disabled:opacity-60"
                            onClick={() => {
                              if (isLocked) return;
                              setSelectedCommitShas((prev) => {
                                const nextSet = new Set(prev.map((sha) => sha.trim()).filter(Boolean));
                                if (nextSet.has(commit.sha)) nextSet.delete(commit.sha);
                                else nextSet.add(commit.sha);
                                return Array.from(nextSet);
                              });
                            }}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                  <Checkbox
                                    checked={checked}
                                    disabled={isLocked}
                                    onClick={(event) => event.stopPropagation()}
                                    onCheckedChange={(next) => {
                                      const shouldSelect = Boolean(next);
                                      setSelectedCommitShas((prev) => {
                                        const nextSet = new Set(prev.map((sha) => sha.trim()).filter(Boolean));
                                        if (shouldSelect) nextSet.add(commit.sha);
                                        else nextSet.delete(commit.sha);
                                        return Array.from(nextSet);
                                      });
                                    }}
                                  />
                                  <span className="inline-flex items-center gap-2 text-xs font-mono text-muted-foreground" title={commit.sha}>
                                    <GitCommit className="w-4 h-4" />
                                    {commit.sha.slice(0, 7)}
                                  </span>
                                </div>
                                <p className="mt-2 text-sm text-muted-foreground break-words">{commit.message}</p>
                              </div>

                              <div className="shrink-0">
                                {isLocked ? (
                                  <Badge variant="outline" className="text-xs">
                                    {isInvoiced ? 'Invoiced' : 'Assigned elsewhere'}
                                  </Badge>
                                ) : checked ? (
                                  <Badge variant="outline" className="text-xs text-primary border-primary/40">
                                    Selected
                                  </Badge>
                                ) : (
                                  <span className="text-xs text-muted-foreground">—</span>
                                )}
                              </div>
                            </div>
                          </button>
                        );
                      })
                      )}
                    </div>
                  </div>

                  <div className="hidden sm:block overflow-x-auto">
                    <Table className="min-w-[860px]">
                      <TableHeader>
                        <TableRow className="bg-muted/30">
                          <TableHead className="w-[44px]">&nbsp;</TableHead>
                          <TableHead>Commit</TableHead>
                          <TableHead>Message</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredCommits.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} className="py-6 text-center text-sm text-muted-foreground">
                              No matching commits.
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredCommits.map((commit) => {
                          const otherWorkorderId = (commit.workorderId ?? '').trim();
                            const isLocked = isInvoiced || (!!otherWorkorderId && otherWorkorderId !== workorder?.id);
                          const checked = normalizedSelected.has(commit.sha);

                          return (
                            <TableRow key={commit.sha}>
                              <TableCell>
                                <Checkbox
                                  checked={checked}
                                  disabled={isLocked}
                                  onCheckedChange={(next) => {
                                    const shouldSelect = Boolean(next);
                                    setSelectedCommitShas((prev) => {
                                      const nextSet = new Set(prev.map((sha) => sha.trim()).filter(Boolean));
                                      if (shouldSelect) nextSet.add(commit.sha);
                                      else nextSet.delete(commit.sha);
                                      return Array.from(nextSet);
                                    });
                                  }}
                                />
                              </TableCell>
                              <TableCell className="text-xs font-mono" title={commit.sha}>
                                <span className="inline-flex items-center gap-2">
                                  <GitCommit className="w-4 h-4 text-muted-foreground" />
                                  {commit.sha.slice(0, 7)}
                                </span>
                              </TableCell>
                              <TableCell className="text-xs text-muted-foreground max-w-[520px] truncate" title={commit.message}>
                                {commit.message}
                              </TableCell>
                              <TableCell>
                                {isLocked ? (
                                  <Badge variant="outline" className="text-xs">
                                    {isInvoiced ? 'Invoiced' : 'Assigned elsewhere'}
                                  </Badge>
                                ) : checked ? (
                                  <Badge variant="outline" className="text-xs text-primary border-primary/40">
                                    Selected
                                  </Badge>
                                ) : (
                                  <span className="text-xs text-muted-foreground">—</span>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button type="button" onClick={onSave} disabled={!workorder || !canEditWorkorder}>
            {saving ? 'Saving…' : 'Save Git order'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
